/**
 * Case Settings Tab
 *
 * Displays and allows editing of case settings with operational fields.
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { ArtefactTemplateManager } from '../../components/collections/ArtefactTemplateManager'
import {
  ClientSection,
  DateTimeSection,
  EditFooter,
  ErrorState,
  EvidenceSection,
  LoadingState,
  SettingsHeader,
} from './case-settings/case-settings-tab.parts'
import { useCaseSettingsTab } from './case-settings/use-case-settings-tab'

interface CaseSettingsTabProps {
  caseId: string
}

export function CaseSettingsTab({ caseId }: CaseSettingsTabProps): ReactElement {
  const { t } = useTranslation('cases')
  const state = useCaseSettingsTab(caseId)

  if (state.loading) return <LoadingState t={t} />
  if (state.error)
    return (
      <ErrorState
        error={state.error}
        onRetry={() => {
          void state.refetch()
        }}
        t={t}
      />
    )

  const sectionProps = {
    formData: state.formData,
    setFormData: state.setFormData,
    editMode: state.editMode,
    isInherited: state.isInherited,
    t,
  }

  return (
    <div className="space-y-6">
      <SettingsHeader editMode={state.editMode} onEdit={state.handleEdit} t={t} />
      {state.settings && (
        <div className="space-y-8">
          <ClientSection settings={state.settings} {...sectionProps} />
          <EvidenceSection settings={state.settings} {...sectionProps} />
          <DateTimeSection settings={state.settings} {...sectionProps} />
        </div>
      )}
      <div className="border-t border-slate-200 pt-6 dark:border-slate-700">
        <ArtefactTemplateManager
          templates={state.templates}
          loading={state.templatesLoading}
          scope="case"
          caseId={caseId}
          userRole="case_user"
          onDelete={(templateId) => state.deleteCase(caseId, templateId)}
          onRefetch={(...args: Parameters<typeof state.refetchTemplates>) => {
            void state.refetchTemplates(...args)
          }}
        />
      </div>
      {state.editMode && (
        <EditFooter
          updating={state.updating}
          onCancel={state.handleCancel}
          onSave={() => {
            void state.handleSave()
          }}
          t={t}
        />
      )}
    </div>
  )
}
